import { wmsApi } from '../../../config/apiClient';
import { ACCOUNTING_API } from '../../../config/api';

export interface CreateSmartWarehouseDto {
  Name: string;
  Address?: string;
  Phone?: string;
  ResponsiblePerson?: string;
  Description?: string;
  IsDefault: boolean;
  BranchesId?: string;
  LocationModel: string;
  TotalCapacity?: number;
  DefaultRoutingStrategy: string;
}

export interface UpdateSmartWarehouseDto extends CreateSmartWarehouseDto {
  Id: string;
}

export interface SmartWarehouseListItem {
  id: string;
  warehouseName: string;
  isDefault?: boolean;
  isActive?: boolean;
  warehouseType?: string;
}

export interface SmartWarehouseDetail {
  id: string;
  name: string;
  locationModel?: string;
  locationCount?: number;
  activeLocationCount?: number;
  inStockItemCount?: number;
  totalStockQuantity?: number;
  totalPickableLocations?: number;
  occupiedLocations?: number;
  emptyLocations?: number;
  occupancyRatePercent?: number;
}

function normalizeWarehouseDetail(raw: any): SmartWarehouseDetail | null {
  if (!raw) return null;
  return {
    id: raw.id || raw.Id,
    name: raw.name || raw.Name || '',
    locationModel: raw.locationModel ?? raw.LocationModel,
    locationCount: raw.locationCount ?? raw.LocationCount,
    activeLocationCount: raw.activeLocationCount ?? raw.ActiveLocationCount,
    inStockItemCount: raw.inStockItemCount ?? raw.InStockItemCount,
    totalStockQuantity: raw.totalStockQuantity ?? raw.TotalStockQuantity,
    totalPickableLocations: raw.totalPickableLocations ?? raw.TotalPickableLocations,
    occupiedLocations: raw.occupiedLocations ?? raw.OccupiedLocations,
    emptyLocations: raw.emptyLocations ?? raw.EmptyLocations,
    occupancyRatePercent: raw.occupancyRatePercent ?? raw.OccupancyRatePercent,
  };
}

function normalizeWarehouseList(responseData: any): SmartWarehouseListItem[] {
  const rawList = responseData?.Data ?? responseData?.data ?? responseData;
  if (!Array.isArray(rawList)) return [];

  return rawList.map((item: any) => ({
    id: item.id || item.Id,
    warehouseName: item.warehouseName || item.WarehouseName || item.name || item.Name || 'Depo',
    isDefault: item.isDefault ?? item.IsDefault,
    isActive: item.isActive ?? item.IsActive,
    warehouseType: item.warehouseType ?? item.WarehouseType,
  }));
}

export const smartWarehouseService = {
  getAll: () =>
    wmsApi.get(ACCOUNTING_API.SMART_WAREHOUSES),

  getAllList: async (): Promise<SmartWarehouseListItem[]> => {
    const response = await wmsApi.get(ACCOUNTING_API.SMART_WAREHOUSES);
    return normalizeWarehouseList(response.data);
  },

  getById: (id: string) =>
    wmsApi.get(ACCOUNTING_API.SMART_WAREHOUSE_BY_ID(id)),

  getByIdDetail: async (id: string): Promise<SmartWarehouseDetail | null> => {
    const response = await wmsApi.get(ACCOUNTING_API.SMART_WAREHOUSE_BY_ID(id));
    const raw = response.data?.Data ?? response.data?.data;
    return normalizeWarehouseDetail(raw);
  },

  create: (data: CreateSmartWarehouseDto) =>
    wmsApi.post(ACCOUNTING_API.SMART_WAREHOUSE_CREATE, data),

  update: (data: UpdateSmartWarehouseDto) =>
    wmsApi.post(ACCOUNTING_API.SMART_WAREHOUSE_UPDATE, data),

  delete: (id: string) =>
    wmsApi.post(ACCOUNTING_API.SMART_WAREHOUSE_DELETE, { Id: id }),

  getSummary: (id: string) =>
    wmsApi.get(ACCOUNTING_API.SMART_WAREHOUSE_SUMMARY(id)),

  // Entry/Exit CRUD
  getEntryExits: (warehouseId: string) =>
    wmsApi.get(ACCOUNTING_API.SW_ENTRY_EXIT(warehouseId)),

  createEntryExit: (warehouseId: string, data: { NodeType: string; Name?: string; GridX: number; GridY: number }) =>
    wmsApi.post(ACCOUNTING_API.SW_ENTRY_EXIT_CREATE(warehouseId), data),

  updateEntryExit: (warehouseId: string, data: { Id: string; NodeType: string; Name?: string; GridX: number; GridY: number }) =>
    wmsApi.post(ACCOUNTING_API.SW_ENTRY_EXIT_UPDATE(warehouseId), data),

  deleteEntryExit: (warehouseId: string, id: string) =>
    wmsApi.post(ACCOUNTING_API.SW_ENTRY_EXIT_DELETE(warehouseId), { Id: id }),

  // Yürünebilir graf (WarehouseGraphNodes/Edges) — Faz 3: kaydet sonrası otomatik tetiklenir,
  // engelleri (Type='Obstacle') hesaba katarak edge'leri (IsWalkable=true/false) yeniden inşa eder.
  rebuildGraph: (warehouseId: string) =>
    wmsApi.post(ACCOUNTING_API.WMS_WAREHOUSE_REBUILD_GRAPH(warehouseId)),
};
